import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { investigationRows } from "./investigations";
import { infusionRate, concLabel } from "./infusion";
import { PILLARS } from "./treatment";

const RED = [185, 28, 28];
const WASHINGTON = "Washington Manual";

function createWriter(doc) {
  const W = doc.internal.pageSize.getWidth();
  const w = { doc, W, y: 88 };
  w.heading = (t) => {
    if (w.y > 760) { doc.addPage(); w.y = 50; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...RED);
    doc.text(t.toUpperCase(), 40, w.y); doc.setDrawColor(...RED); doc.line(40, w.y + 4, W - 40, w.y + 4);
    doc.setTextColor(20); w.y += 18;
  };
  w.para = (t) => {
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
    doc.splitTextToSize(String(t), W - 80).forEach((ln) => { if (w.y > 790) { doc.addPage(); w.y = 50; } doc.text(ln, 40, w.y); w.y += 13; });
    w.y += 4;
  };
  w.bullets = (items) => (items || []).forEach((x) => w.para(`• ${x}`));
  w.table = (head, body) => {
    autoTable(doc, { startY: w.y, head: [head], body, margin: { left: 40, right: 40 }, styles: { fontSize: 8.5, cellPadding: 4 },
      headStyles: { fillColor: RED }, theme: "grid" });
    w.y = doc.lastAutoTable.finalY + 14;
  };
  w.section = (title, head, rows) => { if (rows.length) { w.heading(title); w.table(head, rows); } };
  return w;
}

const kv = (obj) => Object.entries(obj || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k.replace(/_/g, " "), String(v)]);
const dash = (v) => v || "-";
const orDash = (v) => v ?? "-";
const RH = { Positive: "Rh+", Negative: "Rh-" };

function writeBanner({ doc, W }) {
  doc.setFillColor(...RED); doc.rect(0, 0, W, 64, "F");
  doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.text("CritiCalc AI", 40, 32);
  doc.setFontSize(10); doc.setFont("helvetica", "normal");
  doc.text("Critical Care Diagnostic & Treatment Report", 40, 50);
  doc.text(new Date().toLocaleString(), W - 40, 50, { align: "right" });
  doc.setTextColor(20);
}

function writePatientAndHistory(w, s) {
  const p = s.patient;
  w.heading("Patient");
  const bg = p.blood_group ? `${p.blood_group} ${RH[p.rh] || ""}` : "-";
  w.para(`Name/ID: ${dash(p.name)}    Age: ${dash(p.age)}    Sex: ${dash(p.sex)}    Blood group: ${bg}`);
  w.section("History", ["Section", "Details"], kv(s.history));
  const qa = s.followup.questions.filter((q) => s.followup.answers[q.id]).map((q) => [q.question, s.followup.answers[q.id]]);
  w.section("Focused History (AI follow-up)", ["Question", "Answer"], qa);
  if (s.tentative?.ddx) w.section("Tentative Differential Diagnosis", ["Diagnosis", "Likelihood"], s.tentative.ddx.map((d) => [d.diagnosis, d.likelihood]));
}

const fmtOr = (cond, text) => (cond ? text : "-");

function writeVitals(w, v, sc) {
  w.heading("Vitals & Severity Scores");
  w.table(["Parameter", "Value"], [
    ["HR / BP / MAP", `${dash(v.hr)} bpm / ${dash(v.sbp)}/${dash(v.dbp)} mmHg / ${orDash(sc.map)}`],
    ["RR / Temp / SpO2", `${dash(v.rr)} /min / ${dash(v.temp)} °C / ${dash(v.spo2)} %`],
    ["Height / Weight / BMI", `${dash(v.height)} cm / ${dash(v.weight)} kg / ${orDash(sc.bmi)}`],
    ["GCS", fmtOr(sc.gcs != null, `${sc.gcs}/15 (E${v.gcs_e}V${v.gcs_v}M${v.gcs_m})`)],
    ["qSOFA", orDash(sc.qsofa)],
    ["SOFA", fmtOr(sc.sofa != null, `${sc.sofa} (${sc.sofa_entered} components)`)],
    ["APACHE II", fmtOr(sc.apache2 != null, `${sc.apache2} (${sc.apache2_entered}), est. mortality ${sc.apache2_mortality}`)],
    ["Shock Index / Modified SI", `${orDash(sc.shock_index)} / ${orDash(sc.modified_shock_index)}`],
    ["PaO2/FiO2", orDash(sc.pf_ratio)],
  ]);
}

function writeExamAndInvestigations(w, s) {
  const ex = [...kv(s.exam.general).map(([k, x]) => [`General - ${k}`, x]), ...kv(s.exam.systems)];
  w.section("Examination", ["Finding", "Details"], ex);
  w.section("Investigation Reports", ["Panel", "Test", "Result"], investigationRows(s.investigations));
}

const algoRows = (steps, last) => steps.map((x, i) => [i + 1, x.phase || "", x.step, x.decision || "", x[last] || ""]);

function writeRefined(w, s) {
  const r = s.refined;
  if (!r) return;
  w.heading("Refined Differential Diagnosis");
  if (r.syndrome?.name) w.para(`Presenting syndrome: ${r.syndrome.name} (${r.syndrome.ref || WASHINGTON})`);
  if (r.triage) w.para(`Triage: ${r.triage.category} | ICU admission: ${r.triage.icu_admission}. ${r.triage.rationale || ""}`);
  w.table(["Diagnosis", "Likelihood", "Supporting"], (r.ddx || []).map((d) => [d.diagnosis, d.likelihood, d.supporting || ""]));
  const steps = r.algorithm?.steps || [];
  w.section(`Diagnostic Algorithm - ${r.algorithm?.ref || WASHINGTON}`, ["#", "Phase", "Step", "Decision", "Status"], algoRows(steps, "status"));
  const extra = (r.additional_questions || []).filter((q) => s.extraAnswers?.[q.id]).map((q) => [q.ask, q.question, s.extraAnswers[q.id]]);
  w.section("Additional Questions", ["Asked to", "Question", "Answer"], extra);
  const inv = (r.investigations || []).filter((i) => s.selectedInv.includes(i.name));
  w.section("AI-ordered Investigations", ["Investigation", "Priority", "Result"], inv.map((i) => [i.name, i.priority, s.results[i.name] || "Pending"]));
  if (s.otherResults) w.para(`Other reports: ${s.otherResults}`);
}

function writeFinalDiagnosis(w, f) {
  const d = f.final_diagnosis || {};
  w.heading("Final Diagnosis");
  w.para(`${d.diagnosis} ${d.icd10 ? `(ICD-10: ${d.icd10})` : ""} - Confidence: ${d.confidence}`);
  w.para(d.rationale || "");
  if (f.secondary_diagnoses?.length) w.para(`Secondary: ${f.secondary_diagnoses.join("; ")}`);
  if (f.severity_assessment) w.para(`Severity: ${f.severity_assessment}`);
  const steps = f.algorithm?.steps || [];
  w.section(`Management Algorithm - ${f.algorithm?.ref || WASHINGTON}`, ["#", "Phase", "Step", "Decision", "Timing"], algoRows(steps, "timing"));
  if (f.prognosis?.summary) { w.heading("Prognosis"); w.para(f.prognosis.summary); w.bullets(f.prognosis.factors); }
}

function medRow(m, s) {
  const dz = s.doseEdits?.[m.drug] ?? m.infusion?.dose;
  const rate = infusionRate(m.infusion, s.vitals.weight, dz);
  const inf = m.infusion ? `${orDash(rate)} mL/h (${dz} ${m.infusion.dose_unit}; ${concLabel(m.infusion)})` : "";
  return [m.drug, m.dose, m.route, m.frequency, inf, m.notes || ""];
}

function writeTreatment(w, f, s) {
  const t = f.treatment || {};
  w.heading("Evidence-based Treatment");
  w.table(["Step", "Actions"], PILLARS.map(([k, lbl], i) => [`${i + 1}. ${lbl}`, (t[k] || []).map((x) => `• ${x}`).join("\n") || "Not required currently"]));
  w.para("5. Pharmacotherapy:");
  if (t.medications?.length) w.table(["Drug", "Dose", "Route", "Frequency", "Infusion (mL/h)", "Notes"], t.medications.map((m) => medRow(m, s)));
  [["Definitive care & organ support", t.definitive_care || t.organ_support], ["Nutrition & prophylaxis", t.nutrition_and_prophylaxis]].forEach(([lbl, arr]) => {
    if (arr?.length) { w.para(`${lbl}:`); w.bullets(arr); }
  });
  if (t.disposition) w.para(`Disposition: ${t.disposition}`);
  if (f.references?.length) { w.heading("References"); w.bullets(f.references); }
}

export function buildPDF(s, scores) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const w = createWriter(doc);
  writeBanner(w);
  writePatientAndHistory(w, s);
  writeVitals(w, s.vitals, scores);
  writeExamAndInvestigations(w, s);
  writeRefined(w, s);
  if (s.final) { writeFinalDiagnosis(w, s.final); writeTreatment(w, s.final, s); }
  w.heading("Disclaimer");
  w.para("AI-generated clinical decision support based on Vincent's Textbook of Critical Care and The Washington Manual of Critical Care. It does not replace the judgement of a qualified physician. Verify all doses before administration. No patient data is stored by CritiCalc AI.");
  return doc;
}

export function generatePDF(s, scores) {
  buildPDF(s, scores).save(`CritiCalc_Report_${(s.patient.name || "patient").replace(/\W+/g, "_")}_${Date.now()}.pdf`);
}
